/**
 * Every backend path in one place, relative to VITE_API_BASE_URL (which
 * already includes /api/v1). Modules reference these constants instead of
 * hard-coding strings.
 */
export const endpoints = {
  health: '/health',
  ready: '/ready',

  auth: {
    registerStudent: '/auth/register/student',
    registerCompany: '/auth/register/company',
    login: '/auth/login',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    logoutAll: '/auth/logout-all',
    me: '/auth/me',
    deleteAccount: '/auth/me/delete',
    sendVerification: '/auth/verify-email/send',
    verifyEmail: '/auth/verify-email',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    checkResetCode: '/auth/reset-password/check',
    changePassword: '/auth/change-password',
  },

  catalog: {
    universities: '/catalog/universities',
    industries: '/catalog/industries',
    jobCategories: '/catalog/job-categories',
    skills: '/catalog/skills',
    stats: '/catalog/stats',
    featuredCompanies: '/catalog/featured-companies',
    plans: '/catalog/plans',
    companyPerks: '/catalog/company-perks',
  },

  students: {
    me: '/students/me',
    completion: '/students/me/completion',
    photo: '/students/me/photo',
    dashboard: '/students/me/dashboard',
    analytics: '/students/me/analytics',
    careerGoalSuggest: '/students/me/career-goal/suggest',
    careerPath: '/students/me/career-path',
    careerRoadmap: '/students/me/career-roadmap',
    careerCvCheck: '/students/me/career-path/cv-check',
    universityEmail: '/students/me/university-email',
    verifyUniversityEmail: '/students/me/university-email/verify',
    education: '/students/me/education',
    educationById: (id) => `/students/me/education/${id}`,
    educationDiploma: (id) => `/students/me/education/${id}/diploma`,
    educationDiplomaDownload: (id) => `/students/me/education/${id}/diploma/download`,
    experience: '/students/me/experience',
    experienceById: (id) => `/students/me/experience/${id}`,
    skills: '/students/me/skills',
    skillById: (id) => `/students/me/skills/${id}`,
    certifications: '/students/me/certifications',
    certificationById: (id) => `/students/me/certifications/${id}`,
    certificationFile: (id) => `/students/me/certifications/${id}/file`,
    certificationFileDownload: (id) => `/students/me/certifications/${id}/file/download`,
    portfolio: '/students/me/portfolio',
    portfolioById: (id) => `/students/me/portfolio/${id}`,
    resumes: '/students/me/resumes',
    resumeById: (id) => `/students/me/resumes/${id}`,
    resumeDownload: (id) => `/students/me/resumes/${id}/download`,
    resumePrimary: (id) => `/students/me/resumes/${id}/primary`,
    resumeParse: (id) => `/students/me/resumes/${id}/parse`,
    resumeParseApply: (id) => `/students/me/resumes/${id}/parse/apply`,
    resumeReview: (id) => `/students/me/resumes/${id}/review`,
    resumeScore: (id) => `/students/me/resumes/${id}/score`,
    resumeGapAnalysis: (id) => `/students/me/resumes/${id}/gap-analysis`,
    publicPortfolio: '/students/me/public-portfolio',
    publicPortfolioRegenerate: '/students/me/public-portfolio/regenerate',
  },

  // Public, no login: a student's shared portfolio page.
  portfolio: {
    bySlug: (slug) => `/portfolio/${encodeURIComponent(slug)}`,
    cv: (slug) => `/portfolio/${encodeURIComponent(slug)}/cv`,
  },

  messages: {
    root: '/messages',
    unreadCount: '/messages/unread-count',
    byApplication: (id) => `/messages/application/${id}`,
    byId: (id) => `/messages/${id}`,
    attachments: (id) => `/messages/${id}/attachments`,
    attachmentDownload: (id, attachmentId) =>
      `/messages/${id}/attachments/${attachmentId}/download`,
    read: (id) => `/messages/${id}/read`,
    report: (id) => `/messages/${id}/report`,
  },

  realtime: {
    ticket: '/realtime/ticket',
    socket: '/realtime/ws',
  },

  companies: {
    me: '/companies/me',
    close: '/companies/me/close',
    subscription: '/companies/me/subscription',
    subscriptionPlans: '/companies/me/subscription/plans',
    subscriptionResume: '/companies/me/subscription/resume',
    // Prices a code against a plan without spending it.
    subscriptionCouponPreview: '/companies/me/subscription/coupon/preview',
    invoices: '/companies/me/invoices',
    invoiceById: (id) => `/companies/me/invoices/${id}`,
    ats: '/companies/me/ats',
    atsTest: '/companies/me/ats/test',
    atsPending: '/companies/me/ats/pending',
    atsExport: '/companies/me/ats/export',
    atsHandovers: '/companies/me/ats/handovers',
    atsHandoverDownload: (id) => `/companies/me/ats/handovers/${id}/download`,
    completion: '/companies/me/completion',
    logo: '/companies/me/logo',
    branding: '/companies/me/branding',
    brandingCover: '/companies/me/branding/cover',
    brandingGallery: '/companies/me/branding/gallery',
    brandingGalleryOrder: '/companies/me/branding/gallery/order',
    brandingGalleryById: (id) => `/companies/me/branding/gallery/${id}`,
    verification: '/companies/me/verification',
    members: '/companies/me/members',
    memberById: (id) => `/companies/me/members/${id}`,
    memberPermissions: (id) => `/companies/me/members/${id}/permissions`,
    roles: '/companies/me/roles',
    roleById: (role) => `/companies/me/roles/${role}`,
    access: '/companies/me/access',
    bySlug: (slug) => `/companies/${slug}`,

    internships: '/companies/me/internships',
    internshipCounts: '/companies/me/internships/counts',
    internshipById: (id) => `/companies/me/internships/${id}`,
    internshipCandidateMatches: (id) => `/companies/me/internships/${id}/candidate-matches`,
    internshipPerformance: (id) => `/companies/me/internships/${id}/performance`,
    internshipPublish: (id) => `/companies/me/internships/${id}/publish`,
    internshipClose: (id) => `/companies/me/internships/${id}/close`,

    applications: '/companies/me/applications',
    applicationCounts: '/companies/me/applications/counts',
    applicationById: (id) => `/companies/me/applications/${id}`,
    applicationStatus: (id) => `/companies/me/applications/${id}/status`,
    applicationNote: (id) => `/companies/me/applications/${id}/note`,

    candidates: '/companies/me/candidates',
    candidateSearch: '/companies/me/candidates/search',
    candidateTags: '/companies/me/candidates/tags',
    candidateById: (id) => `/companies/me/candidates/${id}`,
    candidateTagsById: (id) => `/companies/me/candidates/${id}/tags`,
    candidateNotes: (id) => `/companies/me/candidates/${id}/notes`,
    candidateNoteById: (id, noteId) => `/companies/me/candidates/${id}/notes/${noteId}`,

    interviews: '/companies/me/interviews',
    interviewCounts: '/companies/me/interviews/counts',
    interviewCalendar: '/companies/me/interviews/calendar',
    interviewById: (id) => `/companies/me/interviews/${id}`,
    interviewCancel: (id) => `/companies/me/interviews/${id}/cancel`,
    interviewClose: (id) => `/companies/me/interviews/${id}/close`,
    interviewFeedback: (id) => `/companies/me/interviews/${id}/feedback`,
    interviewRescheduleAccept: (id) => `/companies/me/interviews/${id}/reschedule-request/accept`,
    interviewRescheduleDecline: (id) => `/companies/me/interviews/${id}/reschedule-request/decline`,

    analytics: '/companies/me/analytics',
    analyticsOverview: '/companies/me/analytics/overview',
    analyticsTrends: '/companies/me/analytics/trends',
    analyticsCandidates: '/companies/me/analytics/candidates',
    analyticsOutcomes: '/companies/me/analytics/outcomes',

    offers: '/companies/me/offers',
    offerCounts: '/companies/me/offers/counts',
    offerById: (id) => `/companies/me/offers/${id}`,
    offerSend: (id) => `/companies/me/offers/${id}/send`,
    offerWithdraw: (id) => `/companies/me/offers/${id}/withdraw`,
  },

  interviews: {
    root: '/interviews',
    byId: (id) => `/interviews/${id}`,
    respond: (id) => `/interviews/${id}/respond`,
    rescheduleRequest: (id) => `/interviews/${id}/reschedule-request`,
  },

  offers: {
    root: '/offers',
    byId: (id) => `/offers/${id}`,
    accept: (id) => `/offers/${id}/accept`,
    decline: (id) => `/offers/${id}/decline`,
  },

  notifications: {
    root: '/notifications',
    unreadCount: '/notifications/unread-count',
    preferences: '/notifications/preferences',
    readAll: '/notifications/read-all',
    read: (id) => `/notifications/${id}/read`,
    byId: (id) => `/notifications/${id}`,
  },

  admin: {
    overview: '/admin/overview',
    verifications: '/admin/verifications',
    approveCompany: (id) => `/admin/verifications/${id}/approve`,
    rejectCompany: (id) => `/admin/verifications/${id}/reject`,
    studentVerifications: '/admin/verifications/students',
    approveStudent: (id) => `/admin/verifications/students/${id}/approve`,
    rejectStudent: (id) => `/admin/verifications/students/${id}/reject`,
    // `kind` is 'diploma' (id = education entry), 'certification'
    // (id = certification) or 'resume' (id = CV).
    documentVerifications: '/admin/verifications/documents',
    documentVerificationCounts: '/admin/verifications/documents/counts',
    documentDownload: (kind, id) => `/admin/verifications/documents/${kind}/${id}/download`,
    approveDocument: (kind, id) => `/admin/verifications/documents/${kind}/${id}/approve`,
    rejectDocument: (kind, id) => `/admin/verifications/documents/${kind}/${id}/reject`,
    featureCompany: (id) => `/admin/companies/${id}/featured`,
    companies: '/admin/companies',
    companyById: (id) => `/admin/companies/${id}`,
    companyRestore: (id) => `/admin/companies/${id}/restore`,
    students: '/admin/students',
    studentById: (id) => `/admin/students/${id}`,
    recruiters: '/admin/recruiters',
    // The workspace seat, not the account -- see the admin section of docs/api.md.
    recruiterById: (memberId) => `/admin/recruiters/${memberId}`,
    internships: '/admin/internships',
    internshipById: (id) => `/admin/internships/${id}`,
    approveInternship: (id) => `/admin/internships/${id}/approve`,
    rejectInternship: (id) => `/admin/internships/${id}/reject`,
    recruitmentApplications: '/admin/recruitment/applications',
    recruitmentApplicationById: (id) => `/admin/recruitment/applications/${id}`,
    recruitmentInterviews: '/admin/recruitment/interviews',
    recruitmentOffers: '/admin/recruitment/offers',
    recruitmentOfferLetter: (id) => `/admin/recruitment/offers/${id}/letter`,
    // Rejection stops a listing going up; this stops one that already did.
    closeInternship: (id) => `/admin/internships/${id}/close`,
    // Oversight only -- there is no admin write on an application.
    applications: '/admin/applications',
    users: '/admin/users',
    userStatus: (id) => `/admin/users/${id}/status`,
    impersonate: (id) => `/admin/users/${id}/impersonate`,
    verifyEmail: (id) => `/admin/users/${id}/verify-email`,
    reports: '/admin/reports',
    resolveReport: (id) => `/admin/reports/${id}/resolve`,
    dismissReport: (id) => `/admin/reports/${id}/dismiss`,
    audit: '/admin/audit',
    analyticsOverview: '/admin/analytics/overview',
    analyticsGrowth: '/admin/analytics/growth',
    analyticsSegments: '/admin/analytics/segments',
    analyticsRevenue: '/admin/analytics/revenue',
    analyticsSubscriptions: '/admin/analytics/subscriptions',
    analyticsVerification: '/admin/analytics/verification',
    analyticsListings: '/admin/analytics/listings',
    analyticsAi: '/admin/analytics/ai',
    analyticsExport: '/admin/analytics/export',

    // Reference data. `kind` is the path segment, so one set of functions
    // serves all four lists instead of four near-identical sets.
    masterData: (kind) => `/admin/master-data/${kind}`,
    masterDataItem: (kind, id) => `/admin/master-data/${kind}/${id}`,
    masterDataState: (kind, id, state) => `/admin/master-data/${kind}/${id}/${state}`,
    skillAliases: (id) => `/admin/master-data/skills/${id}/aliases`,
    skillAlias: (id, aliasId) => `/admin/master-data/skills/${id}/aliases/${aliasId}`,
    skillMerge: (id) => `/admin/master-data/skills/${id}/merge`,
    billingPlans: '/admin/billing/plans',
    billingPlanByCode: (code) => `/admin/billing/plans/${code}`,
    billingSubscriptions: '/admin/billing/subscriptions',
    billingSubscriptionById: (id) => `/admin/billing/subscriptions/${id}`,
    billingSubscriptionPlan: (id) => `/admin/billing/subscriptions/${id}/plan`,
    billingSubscriptionCancel: (id) => `/admin/billing/subscriptions/${id}/cancel`,
    billingSubscriptionResume: (id) => `/admin/billing/subscriptions/${id}/resume`,
    billingOverview: '/admin/billing/overview',
    billingInvoices: '/admin/billing/invoices',
    billingInvoiceMarkPaid: (id) => `/admin/billing/invoices/${id}/mark-paid`,
    billingInvoiceVoid: (id) => `/admin/billing/invoices/${id}/void`,
    billingRunRenewals: '/admin/billing/renewals/run',
    // Coupons live under /admin/billing because a coupon is pricing
    // configuration -- the same kind of object as a plan, same permission.
    coupons: '/admin/billing/coupons',
    couponByCode: (code) => `/admin/billing/coupons/${code}`,
    couponRedemptions: (code) => `/admin/billing/coupons/${code}/redemptions`,
    integrationSettings: '/admin/integrations/settings',
    integrationSeam: (seam) => `/admin/integrations/settings/${seam}`,
    integrationSeamTest: (seam) => `/admin/integrations/settings/${seam}/test`,
    atsProviders: '/admin/ats/providers',
    atsConnections: '/admin/ats/connections',
    atsRun: '/admin/ats/run',
    messages: '/admin/messages',
    messageById: (id) => `/admin/messages/${id}`,
  },

  applications: {
    root: '/applications',
    byId: (id) => `/applications/${id}`,
    withdraw: (id) => `/applications/${id}/withdraw`,
  },

  // Anybody verified may file one; deciding them is under `admin` above.
  reports: {
    root: '/reports',
    mine: '/reports/mine',
  },

  internships: {
    root: '/internships',
    facets: '/internships/facets',
    saved: '/internships/saved',
    recommendations: '/internships/recommendations',
    byId: (identifier) => `/internships/${identifier}`,
    match: (id) => `/internships/${id}/match`,
    resumeMatch: (id) => `/internships/${id}/resume-match`,
    save: (id) => `/internships/${id}/save`,
  },
};
